/**
 * Bilder werden vor dem Speichern verkleinert. Ein Handyfoto hat schnell 5 MB;
 * unterwegs über eine fremde Mobilfunkverbindung ist das der Unterschied
 * zwischen "geht" und "geht nicht".
 */

/**
 * Längste Kante des gespeicherten Bildes.
 *
 * 2400 und nicht weniger, weil diese Bilder gedruckt werden sollen. Die
 * Rechnung: Druckgrösse ist Pixel geteilt durch dpi. Bei 1600 Pixeln und
 * echten 300 dpi bleiben 13,5 cm lange Kante - Postkarte, ein Viertel einer
 * A4-Seite. Ein halbseitiges Foto läge bei rund 150 dpi und sieht auf Papier
 * matschig aus. 2400 Pixel geben bei 300 dpi gut 20 cm, also ein ganzseitiges
 * Bild auf A5 in voller Qualität.
 *
 * Der Preis sind grob doppelt so grosse Dateien. Bei acht Bildern je Etappe und
 * sechzehn Etappen macht das ein paar Dutzend Megabyte - vertretbar gegen die
 * Aussicht, hinterher ein unscharfes Reisebuch in der Hand zu halten. Was
 * einmal klein gespeichert wurde, lässt sich später nicht mehr vergrössern.
 */
const MAX_EDGE = 2400
/** JPEG-Qualität. Darunter werden Schilder und Text unleserlich. */
const QUALITY = 0.82

export interface PreparedImage {
  blob: Blob
  width: number
  height: number
}

/**
 * Verkleinert ein Bild auf MAX_EDGE und wandelt es in JPEG um.
 *
 * Nutzt createImageBitmap, weil das anders als ein img-Element die EXIF-Drehung
 * berücksichtigt und nicht am Hauptthread hängt.
 */
export async function prepareImage(file: Blob): Promise<PreparedImage> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Der Browser stellt keinen 2D-Kontext bereit')
    ctx.drawImage(bitmap, 0, 0, width, height)

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY),
    )
    if (!blob) throw new Error('Das Bild liess sich nicht umwandeln')

    return { blob, width, height }
  } finally {
    bitmap.close()
  }
}

/** Wandelt einen Blob in eine Data-URL, für den Export. */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('Datei nicht lesbar'))
    reader.readAsDataURL(blob)
  })
}

/** Wandelt eine Data-URL zurück in einen Blob, für den Import. */
export async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl)
  return response.blob()
}
